import {
    getFeedDashboard,
    getFeedDashboardCoachDetail,
    getCenterCoachRanking,
    getCenterCoachMetricEvidence,
    getReRegistrationSuccessMembers,
    GetFeedDashboardCoachDetailDataType,
    GetFeedDashboardDataType,
    GetCenterCoachRankingDataType,
    GetCenterCoachMetricEvidenceDataType,
    GetReRegistrationSuccessMembersDataType,
    ReRegistrationSuccessMemberStatus,
} from '../api/feedDashboard';
import type { CenterCoachEvidenceFilter, CenterCoachMetricKey } from '../api/feedDashboard';
import { getItemWithExpiration } from '../lib/utils';

export type JournalWriteBucket = {
    target: number;
    written: number;
    rate?: number | null;
};

export type JournalWriteBreakdown = {
    total: JournalWriteBucket;
    pt: JournalWriteBucket;
    group: JournalWriteBucket;
};

export const fetchGetFeedDashboard = (params: GetFeedDashboardDataType) => ({
    queryKey: ['getFeedDashboardQuery', params.centerId, params.endDate],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');

        return await getFeedDashboard({ ...params, accessToken: authorization });
    },
    onSuccess: ({ data: _data, status }: any) => {
        if (status === 200) {
            const data = _data.data;
            return data;
        } else {
            throw new Error('Failed to fetch feed dashboard data');
        }
    },
});
export type CenterCoachRankingTrainer = {
    coachId: string;
    name: string;
    periodFiltered: {
        revenue: number;
        /** 매출을 구성한 결제·환불 건 수 (금액 0 건 제외) */
        revenueEntryCount?: number;
        prevRevenue: number;
        totalClasses: number;
        noShowCount: number;
        sameDayCancelCount: number;
        sameDayChangeCount: number;
        journalCount: number;
        avgPricePerClass: number | null;
        journalWriteRate: number | null;
        /** 일지 작성 대상 수업 수 (작성률 분모) */
        journalWriteTarget?: number;
        /** 일지 작성 완료 수업 수 (작성률 분자) */
        journalWriteWritten?: number;
        journalWriteBreakdown?: JournalWriteBreakdown;
        newPayMembers: number;
        newPayRevenue: number;
        reRegPayMembers: number;
        reRegPayRevenue: number;
    };
    snapshot: {
        totalMembers: number;
        undeliveredRevenue: number;
        reviewAvg: number;
        reRegistrationRate: number | null;
        reRegisteredMemberCount: number;
        activeMemberCount: number;
        reRegistrationSuccessRate?: number | null;
        reRegistrationSucceededMemberCount?: number;
        reRegistrationEndedMemberCount?: number;
        reRegistrationNotYetMemberCount?: number;
        reRegistrationSuccessDataQuality?: 'EXACT' | 'ESTIMATED' | 'MIXED';
    };
};

export type CenterCoachRankingResponse = {
    trainers: CenterCoachRankingTrainer[];
    period: string;
    periodLabel: string;
    reRegistrationMonth?: string;
    reRegistrationSuccessAsOf?: string;
    /** 하위 집계 실패로 fallback 값이 들어간 원천. 강사앱과 동일하게 확정값으로 노출하면 안 된다. */
    degraded?: string[];
};

export const fetchGetCenterCoachRanking = (params: GetCenterCoachRankingDataType) => ({
    queryKey: ['getCenterCoachRanking', params.centerId, params.period, params.baseDate, params.week, params.day],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        return await getCenterCoachRanking({ ...params, accessToken: authorization });
    },
    onSuccess: ({ data: _data, status }: any) => {
        if (status === 200) return _data.data;
        else throw new Error('Failed to fetch center coach ranking');
    },
});

// ── 지표별 강사 순위 근거(원천 데이터) ──────────────────────────────────────

/** 근거 목록이 요약값을 어떻게 설명하는지 */
export type EvidenceReconcileMode = 'COUNT' | 'AMOUNT_SUM' | 'AVERAGE';

export type CoachMetricEvidenceItem = {
    id: string;
    memberName: string | null;
    title: string;
    description: string;
    /** 분자에 포함된 건인지. 비율 지표에서 false 는 '분모에만 포함'(예: 일지 미작성) */
    includedInNumerator: boolean;
    statusLabel: string;
    /** 금액·평균 지표에서 이 행이 기여한 값 */
    amount?: number;
    lessonDate?: string;
    writtenAt?: string;
    currentSession?: number;
    totalSession?: number;
    journalAction?:
        | {
              sessionType: 'PT' | 'GROUP';
              scheduleId: string;
              postId?: string;
              startTime: string;
          }
        | {
              sessionType: 'NEW_OPPORTUNITY';
              newOpportunityId: string;
              centerId?: string;
              displayName?: string;
              postId?: string;
              startTime: string;
          };
};

export type CoachMetricEvidenceResponse = {
    month: string;
    metricKey: CenterCoachMetricKey;
    coachId: string;
    coachName: string;
    metric: {
        label: string;
        valueType: 'PERCENTAGE' | 'COUNT' | 'CURRENCY' | 'RATING';
        unit: '%' | '회' | '명' | '원' | '건' | '점';
        value: number | null;
        numerator: number;
        denominator: number;
        eligible: boolean;
        /** 랭킹 하위 집계가 실패해 값을 신뢰할 수 없는 상태 */
        degraded: boolean;
    };
    definition: string;
    evidenceLabel: string;
    numeratorLabel: string;
    denominatorLabel: string;
    /** 분모에만 포함된 건을 부르는 말. 목록 행 배지와 같은 단어(예: '미작성') */
    excludedLabel: string;
    /** 월 선택과 무관한 현재 시점 스냅샷 지표 */
    snapshot: boolean;
    items: CoachMetricEvidenceItem[];
    filter: CenterCoachEvidenceFilter;
    evidence: {
        /** 목록이 요약값을 정확히 설명하는지 */
        complete: boolean;
        mode: EvidenceReconcileMode;
        listedNumerator: number;
        listedDenominator: number;
        /** 금액·평균 지표에서 목록 전체 합계 */
        listedTotal: number | null;
        metricNumerator: number;
        metricDenominator: number;
    };
    page: {
        number: number;
        size: number;
        totalItems: number;
        hasNext: boolean;
    };
};

export const fetchGetCenterCoachMetricEvidence = (params: GetCenterCoachMetricEvidenceDataType) => ({
    queryKey: [
        'getCenterCoachMetricEvidence',
        params.centerId,
        params.coachId,
        params.metricKey,
        params.month,
        params.filter ?? 'all',
        params.page ?? 1,
        params.size ?? 20,
    ],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        const response = await getCenterCoachMetricEvidence({ ...params, accessToken: authorization });
        if (response?.status !== 200) throw new Error('Failed to fetch coach metric evidence');
        return response;
    },
    onSuccess: ({ data: responseData, status }: any) => {
        if (status === 200) return responseData.data as CoachMetricEvidenceResponse;
        throw new Error('Failed to fetch coach metric evidence');
    },
});

export type ReRegistrationSuccessDataQuality = 'EXACT' | 'ESTIMATED' | 'MIXED';

export type ReRegistrationSuccessMember = {
    memberId: string;
    memberName: string;
    endedMembershipId: string;
    endedMembershipName: string;
    /** ⚠️ 종료 시각을 못 채운 행이 있다 — 화면은 `formatDate` 로 '-' 를 찍는다 */
    endedAt: string | null;
    previousCoachId: string;
    previousCoachName: string;
    status: ReRegistrationSuccessMemberStatus;
    registeredAt: string | null;
    targetMembershipId: string | null;
    targetMembershipName: string | null;
    newCoachId: string | null;
    newCoachName: string | null;
    dataQuality: ReRegistrationSuccessDataQuality;
};

export type ReRegistrationSuccessMembersResponse = {
    centerId: string;
    cohortMonth: string;
    status: ReRegistrationSuccessMemberStatus | null;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    items: ReRegistrationSuccessMember[];
};

export const fetchGetReRegistrationSuccessMembers = (params: GetReRegistrationSuccessMembersDataType) => ({
    queryKey: [
        'getReRegistrationSuccessMembers',
        params.centerId,
        params.cohortMonth,
        params.status ?? 'ALL',
        params.search ?? '',
        params.page ?? 1,
        params.limit ?? 20,
    ],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');
        const response = await getReRegistrationSuccessMembers({ ...params, accessToken: authorization });
        if (response?.status !== 200) throw new Error('Failed to fetch re-registration success members');
        return response;
    },
    onSuccess: ({ data: responseData, status }: any) => {
        if (status === 200) return responseData.data as ReRegistrationSuccessMembersResponse;
        throw new Error('Failed to fetch re-registration success members');
    },
});

export const fetchGetFeedDashboardCoachDetail = (params: GetFeedDashboardCoachDetailDataType) => ({
    queryKey: ['getFeedDashboardCoachDetailQuery', { ...params }],
    queryFn: async () => {
        const authorization = getItemWithExpiration('authorization');

        return await getFeedDashboardCoachDetail({ ...params, accessToken: authorization });
    },
    onSuccess: ({ data: _data, status }: any) => {
        if (status === 200) {
            const data = _data.data;
            return data;
        } else {
            throw new Error('Failed to fetch feed dashboard data');
        }
    },
});

/**
 * 센터 전체 지표 평균
 */
export type FeedDashboardCenterAverageMetric = {
    /** 센터 일지 작성률 */
    centerReportWriteRate: number | null;
    /** 센터 일지 작성률 전월 대비 증감률 */
    centerReportWriteRateChange: number | null;
    /** 센터 일지 수 */
    centerReportCount: number;
    /** 센터 수업 수 */
    centerScheduleCount: number;
    /** PT·그룹 수업일지 작성률 분해 */
    journalWriteBreakdown?: JournalWriteBreakdown;

    /** 센터 만족률 */
    centerSatisfactionRate: number | null;
    /** 센터 만족률 전월 대비 증감률 */
    centerSatisfactionRateChange: number | null;
    /** 센터 전체 만족도 작성 회원 수 */
    centerSatisfactionMemberCount: number;

    /** 센터 연속등록률 */
    centerReRegistrationRate: number | null;
    /** 센터 연속등록률 전월 대비 증감률 */
    centerReRegistrationRateChange: number | null;
    /** 센터 연속등록한 회원 수 */
    centerReRegisteredMemberCount: number;
    /** 센터 연속등록 대상 회원 수 */
    centerActiveMemberCount: number;

    /** 종료월 기준 재등록 성공률 */
    centerReRegistrationSuccessRate?: number | null;
    /** 종료월 기준 재등록 완료 회원 수 */
    centerReRegistrationSucceededMemberCount?: number;
    /** 종료월 기준 정상 종료 회원 수 */
    centerReRegistrationEndedMemberCount?: number;
    /** 종료월 기준 아직 미재등록 회원 수 */
    centerReRegistrationNotYetMemberCount?: number;
    /** 재등록 성공률 조회 기준 시각 */
    reRegistrationSuccessAsOf?: string;
    /** 재등록 성공률 원천 데이터 정확도 */
    reRegistrationSuccessDataQuality?: ReRegistrationSuccessDataQuality;
};

/**
 * 지표별 강사 순위 정보
 */
export type FeedDashboardCoachRankingMetric = {
    /** 강사 ID */
    coachId: string;
    /** 강사명 */
    name: string;
    /** 강사 프로필 이미지 */
    profileImageUrl: string;
    /** 일지 작성률 */
    reportWriteRate: number | null;
    /** 일지 수 */
    reportCount: number;
    /** 수업 수 */
    scheduleCount: number;
    /** PT·그룹 강사별 수업일지 작성률 분해 */
    journalWriteBreakdown?: JournalWriteBreakdown;

    /** 만족률 */
    satisfactionRate: number | null;
    /** 만족도 작성 회원 수 */
    satisfactionMemberCount: number;

    /** 연속등록률 */
    reRegistrationRate: number | null;
    /** 연속등록한 회원 수 */
    reRegisteredMemberCount: number;
    /** 연속등록 대상 회원 수 */
    activeMemberCount: number;
};

/**
 * 월별 지표 변화 정보
 */
export type FeedDashboardMonthlyMetricChange = {
    /** 센터 일지 작성률 */
    centerReportWriteRate: number | null;
    /** 센터 만족률 */
    centerSatisfactionRate: number | null;
    /** 센터 연속등록률 */
    centerContinuousRegistrationRate: number | null;
    /** 센터 재등록 회원 비중 */
    centerReRegistrationRate?: number | null;
    /** 종료월 기준 재등록 성공률 */
    centerReRegistrationSuccessRate?: number | null;
    /** 종료월 기준 재등록 완료 회원 수 */
    centerReRegistrationSucceededMemberCount?: number;
    /** 종료월 기준 정상 종료 회원 수 */
    centerReRegistrationEndedMemberCount?: number;
    /** 종료월 기준 아직 미재등록 회원 수 */
    centerReRegistrationNotYetMemberCount?: number;
    /** 재등록 성공률 조회 기준 시각 */
    reRegistrationSuccessAsOf?: string;
    /** 재등록 성공률 원천 데이터 정확도 */
    reRegistrationSuccessDataQuality?: ReRegistrationSuccessDataQuality;
    /** 월 표시 문자열 (예: "2024-03") */
    monthString: string;
};

/**
 * 피드 대시보드 응답 데이터
 */
export type GetFeedDashboardResponse = {
    /** 센터 전체 지표 평균 */
    centerAverageMetric: FeedDashboardCenterAverageMetric;
    /** 지표별 강사 순위 */
    coachRankingMetrics: FeedDashboardCoachRankingMetric[];
    /** 최근 4개월 지표 변화 */
    monthlyMetricChanges: FeedDashboardMonthlyMetricChange[];
    /** 구버전 합계 필드를 유지하면서 제공되는 PT·그룹 분해 */
    journalWriteBreakdown?: JournalWriteBreakdown;
};

/**
 * 강사 기본 정보
 */
export interface CoachInfo {
    /** 강사 ID */
    coachId: string;
    /** 강사명 */
    name: string;
    /** 강사 프로필 이미지 */
    profileImageUrl: string;
}

/**
 * 강사 평균 통계 정보
 */
export interface CoachAverageStats {
    /** 강사 일지 작성률 */
    reportWriteRate: number | null;
    /** 강사 일지 작성 수 */
    reportCount: number;
    /** 강사 수업 수 */
    scheduleCount: number;

    /** 센터 일지 작성률 */
    centerReportWriteRate: number | null;
    /** 강사 일지작성률 전월대비 증감률 */
    reportWriteRateChange: number | null;
    /** 센터내 일지작성률 순위 */
    reportWriteRateRank: number;
    /** 센터내 일지작성률 순위 전월대비 증감량 */
    reportWriteRateRankChange: number;

    /** 강사 만족도 지수 */
    satisfactionRate: number | null;
    /** 센터 만족도 지수 */
    centerSatisfactionRate: number | null;
    /** 강사 만족도지수 전월대비 증감률 */
    satisfactionRateChange: number | null;
    /** 센터 내 만족도지수 순위 */
    satisfactionRateRank: number;
    /** 센터 내 만족도 지수 순위 전월대비 증감량 */
    satisfactionRateRankChange: number;
    /** 강사 만족도 작성 회원 수 */
    satisfactionMemberCount: number;

    /** 강사 재등록률 */
    reRegistrationRate: number | null;
    /** 센터 재등록률 */
    centerReRegistrationRate: number | null;
    /** 강사 활성 회원 수 */
    activeMemberCount: number;
    /** 강사 재등록 회원 수 */
    reRegisteredMemberCount: number;
    /** 강사 재등록률 전월대비 증감률 */
    reRegistrationRateChange: number | null;
    /** 센터 내 재등록률 순위 */
    reRegistrationRateRank: number;
    /** 센터 내 재등록률 순위 증감량 */
    reRegistrationRateRankChange: number;

    /** 정상 종료 회원 중 재등록 완료 비율 */
    reRegistrationSuccessRate?: number | null;
    /** 센터 정상 종료 회원 중 재등록 완료 비율 */
    centerReRegistrationSuccessRate?: number | null;
    reRegistrationSucceededMemberCount?: number;
    reRegistrationEndedMemberCount?: number;
    reRegistrationNotYetMemberCount?: number;
    reRegistrationSuccessRateChange?: number | null;
    reRegistrationSuccessRateRank?: number;
    reRegistrationSuccessRateRankChange?: number | null;
    reRegistrationSuccessAsOf?: string;
    reRegistrationSuccessDataQuality?: ReRegistrationSuccessDataQuality;
}

/**
 * 최근 3개월 지표 변화 정보
 */
export interface CoachStatChangesLast3Months {
    /** 강사 일지 작성률 */
    reportWriteRate: number | null;
    /** 센터 일지 작성률 */
    centerReportWriteRate: number | null;
    /** 강사 만족률 */
    satisfactionRate: number | null;
    /** 센터 만족률 */
    centerSatisfactionRate: number | null;
    /** 강사 센터 연속등록률 */
    reRegistrationRate: number | null;
    /** 센터 연속등록률 */
    centerReRegistrationRate: number | null;
    /** 강사·센터 종료 코호트 기준 재등록 성공률 */
    reRegistrationSuccessRate?: number | null;
    centerReRegistrationSuccessRate?: number | null;
    /** 월 표시 문자열 (예: "2024-03") */
    monthString: string;
}

/**
 * 전체 회원 관리 리스트
 */
export interface CoachTotalMemberStatList {
    /** 회원명 */
    memberName: string;
    /** 회원 ID */
    memberId: string;
    /** 일지 작성률 */
    reportWriteRate: number | null;
    /** 최근 수업 리뷰 */
    latestSessionReview?: LatestSessionReview;
    /** 재등록 상태 */
    reRegistrationStatus: string;
    memberType: 'original' | 'temp';
}

export type LatestSessionReview = {
    coachId: string;
    memberId: string;
    postId: string;
    score: number;
    content: string;
    scheduleStartTime: string;
    reviewCreatedAt: string;
    isAnonymous: boolean;
    isDeleted: boolean;
    isCanceled: boolean;
    createdAt: string;
    updatedAt: string;
    coachName: string;
    memberName: string;
    session: number; // 회차
    totalSession: number; // 총 회차 수
};
export type SessionReview = {
    _id: string;
    content: string;
    scheduleStartTime: string; // ISO date string
    isAnonymous: boolean;
    isDeleted: boolean;
    isCanceled: boolean;
    coachId: string;
    memberId: string;
    postId: string;
    score: number;
    reviewCreatedAt: string; // ISO date string
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
    __v: number;
    coachName: string;
    memberName: string;
    session: number; // 회차
    totalSession: number; // 총 회차 수
};

/**
 * 회원별 일지 작성률 리스트
 */
export interface CoachReportWriteRateList {
    /** 회원명 */
    memberName: string;
    /** 일지 작성률 */
    reportWriteRate: number | null;
    /** 일지 작성 수 */
    reportCount: number;
    /** 총 수업수 */
    totalScheduleCount: number;
    /** 최근 수업일 */
    latestScheduleDate: string;
    /** 최근 작성일 */
    latestReportDate: string;
    /** 일지 ID */
    postId: string;
}

/**
 * 해당 달 만족도 리스트
 */
export interface CoachSatisfactionRateList {
    /** 리뷰 정보 */
    coachId: string;
    memberId: string;
    postId: string;
    score: number; // 만족도 점수
    content: string; // 리뷰 내용
    scheduleStartTime: string; // 수업 시작 시간
    reviewCreatedAt: string; // 리뷰 작성 시간
    isAnonymous: boolean;
    isDeleted: boolean;
    isCanceled: boolean;
    createdAt: string;
    updatedAt: string;
    coachName: string;
    memberName: string;
}

/**
 * 피드 대시보드 상세 응답 데이터
 */
export interface FeedDashboardDetailResponse {
    /** 강사 기본 정보 */
    coachInfo: CoachInfo;
    /** 강사 평균 통계 정보 */
    coachAverageStats: CoachAverageStats;
    /** 최근 3개월 지표 변화 */
    coachStatChangesLast3Months: CoachStatChangesLast3Months[];
    /** 전체 회원 관리 리스트 */
    coachTotalMemberStatList: CoachTotalMemberStatList[];
    /** 회원별 일지 작성률 리스트 */
    coachReportWriteRateList: CoachReportWriteRateList[];
    /** 해당 달 만족도 리스트 */
    coachSatisfactionRateList: CoachSatisfactionRateList[];
}
